/**
 * Eigene Statistik-Kachel (BE-06 Teil 3b): eine Datenquelle des Bereichs und
 * eine Darstellung (Zahl, Säulen, Linie, Tabelle), die Ole jederzeit wechseln
 * kann - aus einer Liste wird so eine Statistik. Liegt als Zeile
 * `statistik:<id>` in `oberflaeche` (keine Migration). Rein und getestet.
 */
import { tagesVerlauf, type HabitDays } from '@/domain/habits/daily';
import { erledigtJeWoche } from '@/domain/stats';
import { addDays, differenceInDays, isoWeek, startOfWeek, formatDe } from '@/lib/datetime';
import type { ID, LocalDate, Task } from '@/domain/types';

export type StatistikQuelle = 'erledigt' | 'neu' | 'gewohnheiten';
export type StatistikDarstellung = 'zahl' | 'saeulen' | 'linie' | 'tabelle';

export interface EigeneStatistik {
  /** `statistik:<uuid>` */
  id: string;
  areaId: ID;
  name: string;
  quelle: StatistikQuelle;
  darstellung: StatistikDarstellung;
  updatedAt: string;
}

export const STATISTIK_PRAEFIX = 'statistik:';
export const istStatistikId = (id: string): boolean => id.startsWith(STATISTIK_PRAEFIX);

export const QUELLEN: Record<StatistikQuelle, { label: string; frage: string; einheit: string }> = {
  erledigt: { label: 'Erledigte Aufgaben je Woche', frage: 'Wie viel schaffe ich?', einheit: 'erledigt' },
  neu: { label: 'Neue Aufgaben je Woche', frage: 'Wie viel kommt dazu?', einheit: 'neu' },
  gewohnheiten: { label: 'Gewohnheiten-Quote je Woche', frage: 'Bleibe ich dran?', einheit: '%' },
};
export const DARSTELLUNGEN: Record<StatistikDarstellung, string> = { zahl: 'Zahl', saeulen: 'Säulen', linie: 'Linie', tabelle: 'Tabelle' };

export function statistikAus(roh: unknown): EigeneStatistik | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const r = roh as Record<string, unknown>;
  if (typeof r.id !== 'string' || !istStatistikId(r.id) || typeof r.areaId !== 'string' || typeof r.name !== 'string') return null;
  const quelle = (Object.keys(QUELLEN) as StatistikQuelle[]).find((q) => q === r.quelle) ?? 'erledigt';
  const darstellung = (Object.keys(DARSTELLUNGEN) as StatistikDarstellung[]).find((d) => d === r.darstellung) ?? 'saeulen';
  return { id: r.id, areaId: r.areaId, name: r.name, quelle, darstellung, updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '' };
}

export interface ReihenPunkt { key: string; titel: string; kurz: string; wert: number }

const wocheTitel = (start: LocalDate) => `KW ${isoWeek(start)} (${formatDe(start, 'dd.MM.')}–${formatDe(addDays(start, 6), 'dd.MM.')})`;

/** Die Werte der letzten `wochen` Wochen, aeltester zuerst. */
export function reihe(quelle: StatistikQuelle, daten: { tasks: readonly Task[]; habits: HabitDays[] }, heute: LocalDate, wochen = 8): ReihenPunkt[] {
  const erste = addDays(startOfWeek(heute), -7 * (wochen - 1));
  const starts = Array.from({ length: wochen }, (_, i) => addDays(erste, 7 * i));
  const punkt = (start: LocalDate, wert: number): ReihenPunkt => ({ key: start, titel: wocheTitel(start), kurz: `KW ${isoWeek(start)}`, wert });
  if (quelle === 'erledigt') return erledigtJeWoche([...daten.tasks], heute, wochen).map((w) => punkt(w.start, w.anzahl));
  if (quelle === 'neu') {
    const zaehl = new Array<number>(wochen).fill(0);
    for (const t of daten.tasks) {
      const tag = t.createdAt.slice(0, 10);
      if (tag < erste || tag > heute) continue;
      zaehl[Math.floor(differenceInDays(erste, tag) / 7)]! += 1;
    }
    return starts.map((s, i) => punkt(s, zaehl[i]!));
  }
  const tage = tagesVerlauf(daten.habits, heute, 7 * wochen);
  return starts.map((s, i) => {
    const woche = tage.slice(i * 7, i * 7 + 7);
    const vorgesehen = woche.reduce((a, p) => a + p.vorgesehen, 0);
    return punkt(s, vorgesehen ? Math.round((woche.reduce((a, p) => a + p.erledigt, 0) / vorgesehen) * 100) : 0);
  });
}
